/**
 * Admin User Management Components
 * Provides a comprehensive interface for user administration
 */
import React, { useState, useEffect } from 'react'
import { useAuth } from '../App'
import {
  Users,
  UserPlus,
  MoreHorizontal,
  Eye,
  EyeOff,
  Trash2,
  RefreshCw,
  Copy,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  ShieldAlert,
  User,
  Unlock,
  Lock,
  Key
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiClient } from '../lib/api'
import { showSuccessToast, showErrorToast } from '@/components/NotificationToast'
import { formatDisplayDate } from '../lib/dateUtils'
import { EditUserDialog } from './EditUserDialog'

// Types
interface AdminUser {
  id: string
  email: string
  full_name: string
  mirage_username: string
  mirage_user_id?: number | null
  is_active: boolean
  is_superuser: boolean
  role: 'admin' | 'regular' | 'super_admin'
  status: 'active' | 'pending' | 'disabled'
  first_login_at?: string
  created_at: string
  updated_at: string
  // Password security fields (may not be available from standard API)
  failed_login_attempts?: number
  locked_until?: string
  must_change_password?: boolean
  password_changed_at?: string
}

interface UserListResponse {
  users: AdminUser[]
  total: number
  admin_count: number
}

interface CreateUserResponse {
  user: AdminUser
  password: string
  email_sent: boolean
}

// User Status Badge Component
interface UserStatusBadgeProps {
  status: AdminUser['status']
  size?: 'sm' | 'md' | 'lg'
}

export function UserStatusBadge({ status, size = 'md' }: UserStatusBadgeProps) {
  const variants = {
    active: { variant: 'default' as const, icon: CheckCircle, text: 'Active' },
    pending: { variant: 'secondary' as const, icon: Clock, text: 'Pending' },
    disabled: { variant: 'destructive' as const, icon: XCircle, text: 'Disabled' }
  }

  const config = variants[status]
  const Icon = config.icon

  return (
    <Badge variant={config.variant} className={size === 'sm' ? 'text-xs' : ''}>
      <Icon className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
      {config.text}
    </Badge>
  )
}

// User Role Badge Component
interface UserRoleBadgeProps {
  role: AdminUser['role']
  size?: 'sm' | 'md' | 'lg'
}

export function UserRoleBadge({ role, size = 'md' }: UserRoleBadgeProps) {
  const variants = {
    super_admin: { variant: 'destructive' as const, icon: ShieldAlert, text: 'Super Admin' },
    admin: { variant: 'default' as const, icon: Shield, text: 'Admin' },
    regular: { variant: 'outline' as const, icon: User, text: 'Regular' }
  }

  const config = variants[role]
  const Icon = config.icon

  const extraClass = role === 'super_admin' ? 'bg-violet-600 hover:bg-violet-700 text-white border-violet-600' : ''

  return (
    <Badge variant={config.variant} className={`${size === 'sm' ? 'text-xs' : ''} ${extraClass}`.trim()}>
      <Icon className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
      {config.text}
    </Badge>
  )
}

// Account Security Badge Component
interface AccountSecurityBadgeProps {
  user: AdminUser
  size?: 'sm' | 'md' | 'lg'
}

export function AccountSecurityBadge({ user, size = 'md' }: AccountSecurityBadgeProps) {
  const isLocked = user.locked_until && new Date(user.locked_until) > new Date()
  const mustChangePassword = user.must_change_password
  const hasFailedAttempts = (user.failed_login_attempts || 0) > 0

  if (isLocked) {
    return (
      <Badge variant="destructive" className={size === 'sm' ? 'text-xs' : ''}>
        <Lock className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
        Locked
      </Badge>
    )
  }

  if (mustChangePassword) {
    return (
      <Badge variant="secondary" className={size === 'sm' ? 'text-xs' : ''}>
        <Key className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
        Must Change Password
      </Badge>
    )
  }

  if (hasFailedAttempts) {
    return (
      <Badge variant="secondary" className={size === 'sm' ? 'text-xs' : ''}>
        <Clock className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
        {user.failed_login_attempts} Failed Attempts
      </Badge>
    )
  }

  return null
}

// Helper to copy text to clipboard with fallback
async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    showSuccessToast('Password copied to clipboard!')
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    showSuccessToast('Password copied to clipboard!')
  }
}

// Helper function to check if user account is locked
function isUserAccountLocked(user: AdminUser): boolean {
  return user.locked_until ? new Date(user.locked_until) > new Date() : false
}

// Mirage Link Badge Component
interface MirageLinkBadgeProps {
  user: AdminUser
  size?: 'sm' | 'md' | 'lg'
}

export function MirageLinkBadge({ user, size = 'md' }: MirageLinkBadgeProps) {
  if (user.mirage_user_id) {
    return (
      <Badge variant="default" className={`${size === 'sm' ? 'text-xs' : ''} bg-green-600 text-white`}>
        <CheckCircle className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
        Accounting software: {user.mirage_username}
      </Badge>
    )
  }

  return (
    <Badge variant="secondary" className={`${size === 'sm' ? 'text-xs' : ''} bg-yellow-100 text-yellow-800`}>
      <Clock className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
      No accounting software link
    </Badge>
  )
}

// User Action Buttons Component
interface UserActionButtonsProps {
  user: AdminUser
  adminCount: number
  currentUserId: string
  onUserUpdated: () => Promise<void>
  globalLoading?: boolean
}

export function UserActionButtons({ user, adminCount, currentUserId, onUserUpdated, globalLoading }: UserActionButtonsProps) {
  const [loading, setLoading] = useState(false)
  const isDisabled = loading || globalLoading
  const isSelf = user.id === currentUserId
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [newPassword, setNewPassword] = useState('')

  const isLastAdmin = user.role === 'admin' && adminCount <= 1
  const isAccountLocked = isUserAccountLocked(user)

  const handleStatusToggle = async () => {
    if (isLastAdmin && user.status === 'active') {
      showErrorToast('Cannot disable the last admin user')
      return
    }

    setLoading(true)
    try {
      const newStatus = user.status === 'active' ? 'disabled' : 'active'
      await apiClient.put(`/admin/users/${user.id}/status`, {
        status: newStatus
      })
      await onUserUpdated()
    } catch (error: any) {
      showErrorToast(`Failed to update user status: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (isLastAdmin) {
      showErrorToast('Cannot delete the last admin user')
      return
    }

    setLoading(true)
    try {
      await apiClient.delete(`/admin/users/${user.id}`)
      await onUserUpdated()
      setShowDeleteDialog(false)
    } catch (error: any) {
      showErrorToast(`Failed to delete user: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordReset = async (sendEmail: boolean) => {
    setLoading(true)
    try {
      const response = await apiClient.post(`/admin/users/${user.id}/reset-password`, {
        send_email: sendEmail
      }) as { new_password: string; email_sent: boolean }
      setNewPassword(response.new_password)
      if (!sendEmail) {
        await copyToClipboard(response.new_password)
      }
      setShowPasswordDialog(true)
      await onUserUpdated()
    } catch (error: any) {
      showErrorToast(`Failed to reset password: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleUnlockAccount = async () => {
    setLoading(true)
    try {
      await apiClient.post(`/admin/users/${user.id}/unlock`)
      await onUserUpdated()
      showSuccessToast('Account unlocked successfully')
    } catch (error: any) {
      showErrorToast(`Failed to unlock account: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleForcePasswordChange = async () => {
    setLoading(true)
    try {
      const newValue = !user.must_change_password
      await apiClient.put(`/admin/users/${user.id}/force-password-change`, {
        must_change_password: newValue
      })
      await onUserUpdated()
      showSuccessToast(`Force password change ${newValue ? 'enabled' : 'disabled'}`)
    } catch (error: any) {
      showErrorToast(`Failed to update force password change: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="flex items-center space-x-1">
        {/* Status Toggle - hidden for own account */}
        {!isSelf && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleStatusToggle}
            disabled={isDisabled || (isLastAdmin && user.status === 'active')}
            title={user.status === 'active' ? 'Disable User' : 'Enable User'}
          >
            {user.status === 'active' ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        )}

        {/* More Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" disabled={isDisabled}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handlePasswordReset(true)}>
              <Mail className="mr-2 h-4 w-4" />
              Reset Password (Email)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePasswordReset(false)}>
              <Copy className="mr-2 h-4 w-4" />
              Reset Password (Copy)
            </DropdownMenuItem>
            {isAccountLocked && (
              <DropdownMenuItem onClick={handleUnlockAccount}>
                <Unlock className="mr-2 h-4 w-4" />
                Unlock Account
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleToggleForcePasswordChange}>
              <Key className="mr-2 h-4 w-4" />
              {user.must_change_password ? 'Disable' : 'Enable'} Force Password Change
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setShowDeleteDialog(true)}
              disabled={isLastAdmin}
              className="text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {user.full_name} ({user.email})?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Display Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Password Generated</DialogTitle>
            <DialogDescription>
              A new password has been generated for {user.full_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2">
            <Input value={newPassword} readOnly className="font-mono" />
            <Button size="sm" onClick={() => copyToClipboard(newPassword)}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPasswordDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Create User Dialog Component
interface CreateUserDialogProps {
  onUserCreated: () => void
}

export function CreateUserDialog({ onUserCreated }: CreateUserDialogProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'regular' as 'admin' | 'regular' | 'super_admin',
    send_email: true
  })
  const [createdUser, setCreatedUser] = useState<CreateUserResponse | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await apiClient.post('/admin/users', formData) as CreateUserResponse
      setCreatedUser(response)
      onUserCreated()

      // Reset form
      setFormData({
        full_name: '',
        email: '',
        role: 'regular',
        send_email: true
      })
    } catch (error: any) {
      showErrorToast(`Failed to create user: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button>
            <UserPlus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create a new user account. A secure password will be generated automatically.
            </DialogDescription>
          </DialogHeader>

          {!createdUser ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'admin' | 'regular' | 'super_admin' }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="regular">Regular User</option>
                  <option value="admin">Admin User</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={formData.send_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, send_email: e.target.checked }))}
                />
                <Label htmlFor="send_email">Send password via email</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <div>
                  <p className="font-medium">User created successfully!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {createdUser.user.full_name} ({createdUser.user.email}) has been created.
                    {createdUser.email_sent ? ' Password sent via email.' : ' Password available below.'}
                  </p>
                </div>
              </Alert>

              {!createdUser.email_sent && (
                <div className="space-y-2">
                  <Label>Generated Password</Label>
                  <div className="flex items-center space-x-2">
                    <Input value={createdUser.password} readOnly className="font-mono" />
                    <Button size="sm" onClick={() => copyToClipboard(createdUser.password)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button onClick={() => {
                  setCreatedUser(null)
                  setOpen(false)
                }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

// User List Component
interface UserListProps {
  users: AdminUser[]
  adminCount: number
  currentUserId: string
  onUserUpdated: () => Promise<void>
}

export function UserList({ users, adminCount, currentUserId, onUserUpdated }: UserListProps) {
  const [actionInProgress, setActionInProgress] = useState(false)

  const handleUserUpdated = async () => {
    setActionInProgress(true)
    try {
      await onUserUpdated()
    } finally {
      setActionInProgress(false)
    }
  }
  if (users.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
        <p className="text-muted-foreground">Get started by creating your first user.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {users.map((user) => (
        <Card key={user.id} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 flex-wrap">
                  <h3 className="font-medium">{user.full_name}</h3>
                  <UserRoleBadge role={user.role} size="sm" />
                  <UserStatusBadge status={user.status} size="sm" />
                  <AccountSecurityBadge user={user} size="sm" />
                  <MirageLinkBadge user={user} size="sm" />
                </div>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <p className="text-xs text-muted-foreground">
                  Created: {formatDisplayDate(user.created_at)}
                  {user.first_login_at && (
                    <> • First login: {formatDisplayDate(user.first_login_at)}</>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <EditUserDialog user={user as any} onUserUpdated={handleUserUpdated} />
              <UserActionButtons
                user={user}
                adminCount={adminCount}
                currentUserId={currentUserId}
                onUserUpdated={handleUserUpdated}
                globalLoading={actionInProgress}
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

// Main User Management Panel Component
export function UserManagementPanel() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalUsers, setTotalUsers] = useState(0)
  const [adminCount, setAdminCount] = useState(0)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/admin/users') as UserListResponse
      setUsers(response.users)
      setTotalUsers(response.total)
      setAdminCount(response.admin_count)
    } catch (err: any) {
      setError(err.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  if (loading) {
    return (
      <Card className="p-8 text-center">
        <RefreshCw className="mx-auto h-8 w-8 animate-spin text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Loading users...</p>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" />
        <div>
          <p className="font-medium">Failed to load users</p>
          <p className="text-sm">{error}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={fetchUsers}>
            Try Again
          </Button>
        </div>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-muted-foreground">
            {totalUsers} total users • {adminCount} admins
          </p>
        </div>
        <CreateUserDialog onUserCreated={fetchUsers} />
      </div>

      {/* User List */}
      <UserList
        users={users}
        adminCount={adminCount}
        currentUserId={currentUser?.id ?? ''}
        onUserUpdated={fetchUsers}
      />
    </div>
  )
}
